import React, { useState, useRef, useEffect } from 'react';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { fitToScreen } from '_ui-core-js/utilities/fitToScreen.js';
import { detectBrowser } from '_ui-core-js/utilities/detectBrowser.js';
import { useBreakpointChange } from '_ui-core-js/React/hooks/useBreakpointChange.js';
import { LandingScreen } from './LandingScreen.jsx';
import { LoginScreen } from './LoginScreen.jsx';
import { InstagramFeedGenerationScreen } from './InstagramFeedGenerationScreen.jsx';
import { ColorPaletteSelectionScreen } from './ColorPaletteSelectionScreen.jsx';
import { ColorPaletteGenerationScreen } from './ColorPaletteGenerationScreen.jsx';
import { ColorPalettePresentationScreen } from './ColorPalettePresentionScreen.jsx';
import { UserChoicesSelectionScreen } from './UserChoicesSelectionScreen.jsx';
import { CarColorSelectionScreen } from './CarColorSelectionScreen.jsx';
import { ArtworkGenerationScreen } from './ArtworkGenerationScreen.jsx';
import { ArtworkPresentationScreen } from './ArtworkPresentationScreen.jsx';

export const VIEWS = {
  landing: {
    name: 'landing',
    dataKey: 'landing',
    useFitToScreen: true,
    useAdaptableHeight: false
  },
  login: {
    name: 'login',
    dataKey: 'login',
    useFitToScreen: true,
    useAdaptableHeight: true
  },
  instagramColorPaletteSelection: {
    name: 'instagramColorPaletteSelection',
    dataKey: 'instagramColorPaletteSelection',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true  
  },
  instagramFeedGeneration: {
    name: 'instagramFeedGeneration',
    dataKey: 'instagramFeedGeneration',
    useFitToScreen: false
  },
  colorPaletteSelection: {
    name: 'colorPaletteSelection',
    dataKey: 'colorPaletteSelection',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true  
  },
  colorPaletteGeneration: {
    name: 'colorPaletteGeneration',
    dataKey: 'colorPaletteGeneration',
    useFitToScreen: false
  },
  colorPalettePresentation: {
    name: 'colorPalettePresentation',
    dataKey: 'colorPalettePresentation',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true  
  },
  userChoicesSelection: {
    name: 'userChoicesSelection',
    dataKey: 'userChoicesSelection',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true  
  },
  carColorSelection:  {
    name: 'carColorSelection',
    dataKey: 'carColorSelection',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true
  },
  artworkGeneration: {
    name: 'artworkGeneration',
    dataKey: 'artworkGeneration',
    useFitToScreen: false
  },
  artworkPresentation: {
    name: 'artworkPresentation',
    dataKey: 'artworkPresentation',
    useFitToScreen: true,
    useAdaptableHeight: true,
    usePadding: true  }
};

const COMPONENTS = {
  landing: LandingScreen,
  login: LoginScreen,
  instagramColorPaletteSelection: ColorPaletteSelectionScreen,
  instagramFeedGeneration: InstagramFeedGenerationScreen,
  colorPaletteSelection: ColorPaletteSelectionScreen,
  colorPaletteGeneration: ColorPaletteGenerationScreen,
  colorPalettePresentation: ColorPalettePresentationScreen,
  userChoicesSelection: UserChoicesSelectionScreen,
  carColorSelection: CarColorSelectionScreen,
  artworkGeneration: ArtworkGenerationScreen,
  artworkPresentation: ArtworkPresentationScreen
};

function initConfig(instagram) {
  const code = instagram.instagramAuthCode
  const instagramError = instagram.instagramAuthError 
  const initScreen = !!code || instagramError ? VIEWS.instagramFeedGeneration.name : VIEWS.landing.name;

  return {
    view: initScreen,
    instagramCode: code,
    loggedIn: !!code,
    instagramError
  };
};

export function AiExperience(props) {

  const rootElRef = useRef();
  const fitToScreenRef = useRef();
  const initConfigRef = useRef();
  const isSafariBrowserRef = useRef(detectBrowser() === 'Apple Safari');
  initConfigRef.current = initConfigRef.current ? initConfigRef.current : initConfig(props.instagram);
  const [user] = useState(props.user)
  const [atmosphere, setAtmosphere] = useState(props.atmosphere);
  const [model, setModel] = useState(props.model);
  const [activeView, setActiveView] = useState(initConfigRef.current.view);
  const [loggedIn, setLoggedIn] = useState(initConfigRef.current.loggedIn);
  const [instagramCode, setInstagramCode] = useState(initConfigRef.current.instagramCode);
  const [instagramAuthError, setInstagramAuthError] = useState(initConfigRef.current.instagramError);
  const [error, setError] = useState();
  const [instagramImageFeed, setInstagramImageFeed] = useState([]);
  const [curatedImageFeed, setCuratedImageFeed] = useState(props?.colorPaletteSelection?.images);
  const [curatedColorPaletteGridImages, setCuratedColorPaletteGridImages] = useState([]);
  const [selectedColorPaletteImages, setSelectedColorPaletteImages] = useState([]);
  const [colorPaletteHexValues, setColorPaletteHexValues] = useState([]); // array of arrays of all extracted values from images
  const [colorPaletteHexValuesSelectedIndexes, setColorPaletteHexValuesSelectedIndexes] = useState([]);
  const [colorPalettePayload, setColorPalettePayload] = useState([]);
  const [selectedUserChoicesImages, setSelectedUserChoicesImages] = useState([]);
  const [selectedCarColorCode, setSelectedCarColorCode] = useState();
  const [selectedCarColorLabel, setSelectedCarColorLabel] = useState();
  const [artworkUrl, setArtworkUrl] = useState();
  const [artworkId, setArtworkId] = useState();
  const [showSaveArtworkConfirmationModal, setShowSaveArtworkConfirmationModal] = useState();
  const instagramAppId = props.instagram.instagramAppId;
  const getInstagramAuthenticationCode = props.instagram.getAuthenticationCode;
  const getInstagramPosts = props.instagram.getInstagramPosts;
  const navigationOptions = props.navigationOptions
  const [smallView, setSmallView] = useState(!useBreakpointChange((breakpoint, largerThanPivotBp) => {
    setSmallView(!largerThanPivotBp);
  }, 'md' )[1]);
  const [settings, setSettings] = useState(props.settings);
  const block = 'c-ai-experience';

  const state = {
    atmosphere,
    setAtmosphere,
    model,
    setModel,
    activeView,
    setActiveView,
    loggedIn,
    setLoggedIn,
    instagramCode,
    setInstagramCode,
    error,
    setError,
    instagramImageFeed,
    setInstagramImageFeed,
    curatedImageFeed,
    setCuratedImageFeed,
    curatedColorPaletteGridImages, 
    setCuratedColorPaletteGridImages,
    selectedColorPaletteImages,
    setSelectedColorPaletteImages,
    colorPaletteHexValues,
    setColorPaletteHexValues,
    colorPaletteHexValuesSelectedIndexes,
    setColorPaletteHexValuesSelectedIndexes,
    colorPalettePayload,
    setColorPalettePayload,
    selectedUserChoicesImages,
    setSelectedUserChoicesImages,
    selectedCarColorCode,
    setSelectedCarColorCode,
    selectedCarColorLabel,
    setSelectedCarColorLabel,
    artworkUrl,
    setArtworkUrl,
    artworkId,
    setArtworkId,
    instagramAppId,
    getInstagramAuthenticationCode,
    getInstagramPosts,
    showSaveArtworkConfirmationModal,
    setShowSaveArtworkConfirmationModal,
    navigationOptions,
    smallView,
    setSmallView,
    instagramAuthError, 
    setInstagramAuthError,
    settings, 
    setSettings
  };

  useEffect(() => {
    const { useFitToScreen, useAdaptableHeight } = VIEWS[activeView];

    if (fitToScreenRef.current) {
      fitToScreenRef.current.destroy();
      fitToScreenRef.current = undefined;
    }

    if (useFitToScreen) {
      fitToScreenRef.current = fitToScreen(rootElRef.current, [document.querySelector('.header')], useAdaptableHeight);
      fitToScreenRef.current.init();
    } 
    
    window.scrollTo(0,0);

  }, [activeView]);
  
  
  function handleErrorMsg() {
    if (error) {
      setError(false);
    }
  };

  const activeViewConfig = VIEWS[activeView];
  const Component = COMPONENTS[activeView];
  const data = props[activeViewConfig.dataKey];

  return (
    <div
    className={classNames({
     [`${block} lbx-theme-cool light-theme-lighter`]: true,
     [`${block}--expandable-height`]: activeViewConfig.useFitToScreen && activeViewConfig.useAdaptableHeight,
     [`${block}--use-padding`]: activeViewConfig.usePadding,
     [`${block}--no-min-height`]: !activeViewConfig.useFitToScreen,
	 [`${block}--blur-background`]: showSaveArtworkConfirmationModal,
     'safari-browser': isSafariBrowserRef.current
    })}
    ref={rootElRef} 
    onClick={handleErrorMsg}
    >
      <Component 
      block={block} 
      rootElRef={rootElRef} 
      data={data} 
      state={state}
      user={user}
      />  
    </div>
  )
};

AiExperience.propTypes = {
  landing: propTypes.object
};